/** Analytics (WS6 routes): per-post table (latest metrics + 24 h delta), per-post series, followers per account,
 * aggregates by template / account / artist / song. Every chart has a table with the same numbers. */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { PageHeader } from "../../components/PageHeader";
import { BarChart, LineChart, Sparkline } from "../lib/charts";
import { fmtDelta, fmtNum, fmtWhen } from "../lib/time";
import { Empty, QueryView, Section, TableScroll } from "../lib/ui";
import { intParam, oneOf, type Patch, useUrlState } from "../lib/url";

export const AGG_BY = ["template", "account", "artist", "song"] as const;
export const DAYS = [7, 30, 90] as const;
type By = (typeof AGG_BY)[number];

export type AnalyticsParams = { by: By; days: number; account?: string; post?: number };

export function parseAnalyticsParams(sp: URLSearchParams): AnalyticsParams {
  const d = intParam(sp.get("days"));
  return {
    by: oneOf(sp.get("by"), AGG_BY, "template"),
    days: d && d >= 1 && d <= 365 ? d : 30,
    account: sp.get("account") || undefined,
    post: intParam(sp.get("post")),
  };
}

export function analyticsParamsToPatch(p: AnalyticsParams): Patch {
  return { by: p.by === "template" ? undefined : p.by, days: p.days === 30 ? undefined : p.days, account: p.account, post: p.post };
}

function PostSeries({ postId }: { postId: number }) {
  const q = useQuery({
    queryKey: qk.list("analytics.series", { postId }),
    queryFn: () => unwrap(api.GET("/api/analytics/posts/{post_id}/series", { params: { path: { post_id: postId } } })),
  });
  return (
    <Section id="series" title={`Post #${postId}: plays over time`}>
      <QueryView q={q} what="the series">
        {(pts) => {
          const plays = pts.filter((p) => p.plays != null).map((p) => ({ x: p.t, y: p.plays ?? 0 }));
          if (pts.length === 0) return <Empty>No metrics yet.</Empty>;
          const last = plays.at(-1);
          return (
            <>
              <LineChart points={plays} fmtX={(x) => fmtWhen(x)} testId="series-chart"
                label={`Plays of post #${postId}: ${plays.length} samples${last ? `, latest ${fmtNum(last.y)}` : ""}`} />
              <TableScroll label="Series table">
                <table className="ws7-table" aria-label={`Metrics of post #${postId}`} data-testid="series-table">
                  <thead><tr><th scope="col">When</th><th scope="col" className="num">Plays</th><th scope="col" className="num">Likes</th>
                    <th scope="col" className="num">Comments</th><th scope="col" className="num">Shares</th><th scope="col" className="num">Saves</th></tr></thead>
                  <tbody>
                    {pts.map((p) => (
                      <tr key={p.t}><td>{fmtWhen(p.t)}</td><td className="num">{fmtNum(p.plays)}</td><td className="num">{fmtNum(p.likes)}</td>
                        <td className="num">{fmtNum(p.comments)}</td><td className="num">{fmtNum(p.shares)}</td><td className="num">{fmtNum(p.saves)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            </>
          );
        }}
      </QueryView>
    </Section>
  );
}

function Followers({ days }: { days: number }) {
  const q = useQuery({ queryKey: qk.list("analytics.accounts", { days }), queryFn: () => unwrap(api.GET("/api/analytics/accounts", { params: { query: { days } } })) });
  return (
    <QueryView q={q} what="accounts">
      {(rows) => (rows.length === 0 ? <Empty>No accounts.</Empty> : (
        <TableScroll label="Followers table">
          <table className="ws7-table" aria-label="Followers per account" data-testid="followers-table">
            <thead><tr><th scope="col">Account</th><th scope="col" className="num">Followers</th><th scope="col">Change</th><th scope="col">Trend</th>
              <th scope="col" className="num">Posts</th><th scope="col" className="num">Plays</th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const f = r.followers.map((x) => x.followers);
                const first = f[0];
                const last = f.at(-1);
                return (
                  <tr key={r.account.id} data-testid={`followers-${r.account.id}`}>
                    <th scope="row">{r.account.id} {r.account.handle ?? ""}</th>
                    <td className="num">{fmtNum(last ?? r.account.followers)}</td>
                    <td>{first != null && last != null ? fmtDelta(last - first) : ""}</td>
                    <td><Sparkline values={f} label={`Followers of ${r.account.id}: ${f.map((x) => fmtNum(x)).join(", ") || "no data"}`} /></td>
                    <td className="num">{r.posts}</td>
                    <td className="num">{fmtNum(r.plays)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      ))}
    </QueryView>
  );
}

function Aggregates({ by, days }: { by: By; days: number }) {
  const q = useQuery({ queryKey: qk.list("analytics.aggregate", { by, days }), queryFn: () => unwrap(api.GET("/api/analytics/aggregate", { params: { query: { by, days } } })) });
  return (
    <QueryView q={q} what="aggregates">
      {(rows) => (rows.length === 0 ? <Empty>No posts in this period.</Empty> : (
        <>
          <BarChart rows={rows.map((r) => ({ label: r.label, value: r.plays }))} testId="aggregate-chart"
            label={`Plays by ${by}: ${rows.map((r) => `${r.label} ${fmtNum(r.plays)}`).join("; ")}`} />
          <TableScroll label="Aggregate table">
            <table className="ws7-table" aria-label={`Totals by ${by}`} data-testid="aggregate-table">
              <thead><tr><th scope="col">{by}</th><th scope="col" className="num">Posts</th><th scope="col" className="num">Plays</th>
                <th scope="col" className="num">Likes</th><th scope="col" className="num">Avg plays</th><th scope="col" className="num">Follows Δ</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key}><th scope="row">{r.label}</th><td className="num">{r.posts}</td><td className="num">{fmtNum(r.plays)}</td>
                    <td className="num">{fmtNum(r.likes)}</td><td className="num">{r.avg_plays != null ? fmtNum(Math.round(r.avg_plays)) : ""}</td>
                    <td className="num">{fmtDelta(r.follows_delta)}</td></tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </>
      ))}
    </QueryView>
  );
}

export function AnalyticsView() {
  const announce = useAnnounce();
  const { sp, set: setUrl } = useUrlState();
  const p = parseAnalyticsParams(sp);
  const set = (patch: Patch, msg: string) => {
    setUrl(patch);
    announce(msg);
  };
  const from = Math.floor(Date.now() / 1000 / 3600) * 3600 - p.days * 86400;
  const posts = useQuery({
    queryKey: qk.list("analytics.posts", { account: p.account, days: p.days }),
    queryFn: () => unwrap(api.GET("/api/analytics/posts", { params: { query: { account_id: p.account, from } } })),
  });
  return (
    <div className="ws7-page" data-testid="analytics-page">
      <PageHeader title="Analytics" md={{ page: "analytics", params: { by: p.by, days: p.days, account_id: p.account } }} />
      <form className="ws7-toolbar" aria-label="Analytics view" onSubmit={(e) => e.preventDefault()}>
        <label className="ws7-field">
          Period
          <select value={p.days} data-testid="analytics-days" onChange={(e) => set({ days: Number(e.target.value) === 30 ? undefined : e.target.value }, `Period: last ${e.target.value} days`)}>
            {DAYS.map((d) => <option key={d} value={d}>last {d} days</option>)}
            {!(DAYS as readonly number[]).includes(p.days) && <option value={p.days}>last {p.days} days</option>}
          </select>
        </label>
        <label className="ws7-field">
          Account
          <select value={p.account ?? ""} data-testid="analytics-account" onChange={(e) => set({ account: e.target.value || undefined, post: undefined }, `Account: ${e.target.value || "all"}`)}>
            <option value="">All accounts</option>
            {["a1", "a2", "a3", "a4"].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <fieldset className="ws7-inline" style={{ border: "none", padding: 0, margin: 0 }}>
          <legend className="ws7-field">Totals by</legend>
          {AGG_BY.map((b) => (
            <button key={b} type="button" aria-pressed={p.by === b} data-testid={`agg-by-${b}`} className={p.by === b ? "ws7-primary" : undefined}
              onClick={() => set({ by: b === "template" ? undefined : b }, `Totals by ${b}`)}>
              {b}
            </button>
          ))}
        </fieldset>
      </form>
      <Section id="posts" title="Posts">
        <QueryView q={posts} what="posts">
          {(rows) => (rows.length === 0 ? <Empty>No posts in this period.</Empty> : (
            <TableScroll label="Post metrics table">
              <table className="ws7-table" aria-label="Post metrics" data-testid="posts-table">
                <thead><tr><th scope="col">Post</th><th scope="col">Account</th><th scope="col">Template</th><th scope="col">Posted</th>
                  <th scope="col">Status</th><th scope="col" className="num">Plays</th><th scope="col" className="num">Plays 24 h</th>
                  <th scope="col" className="num">Likes</th><th scope="col" className="num">Comments</th><th scope="col" className="num">Shares</th>
                  <th scope="col" className="num">Saves</th><th scope="col">Series</th></tr></thead>
                <tbody>
                  {rows.map((r: Schemas["PostStats"]) => (
                    <tr key={r.post.id} data-testid={`post-stats-${r.post.id}`} className={p.post === r.post.id ? "ws7-selected" : undefined}>
                      <td>#{r.post.id} {r.post.url ? <a href={r.post.url} target="_blank" rel="noreferrer">TikTok</a> : null}
                        {" "}<Link to={`/renders/${r.post.render_id}`}>render #{r.post.render_id}</Link></td>
                      <td>{r.post.account_id}</td>
                      <td>{r.post.template_id ?? ""}</td>
                      <td>{fmtWhen(r.post.posted_at ?? r.post.t_post)}</td>
                      <td>{r.post.status}</td>
                      <td className="num">{fmtNum(r.latest?.plays)}</td>
                      <td className="num">{fmtDelta(r.plays_24h)}</td>
                      <td className="num">{fmtNum(r.latest?.likes)}</td>
                      <td className="num">{fmtNum(r.latest?.comments)}</td>
                      <td className="num">{fmtNum(r.latest?.shares)}</td>
                      <td className="num">{fmtNum(r.latest?.saves)}</td>
                      <td>
                        <button type="button" data-testid={`show-series-${r.post.id}`} aria-pressed={p.post === r.post.id}
                          aria-label={`Show series of post #${r.post.id}`} onClick={() => set({ post: r.post.id }, `Showing series of post #${r.post.id}`)}>
                          Show series
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          ))}
        </QueryView>
      </Section>
      {p.post != null && <PostSeries key={p.post} postId={p.post} />}
      <div className="ws7-grid2">
        <Section id="followers" title="Followers per account">
          <Followers days={p.days} />
        </Section>
        <Section id="aggregates" title={`Totals by ${p.by}`}>
          <Aggregates by={p.by} days={p.days} />
        </Section>
      </div>
    </div>
  );
}
